import type { Components } from '@mui/material/styles';

import type { Theme } from '../types';

export const MuiPaper = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        backgroundImage: 'none',
        ...(isDark && {
          backgroundColor: 'var(--mui-palette-neutral-900)',
        }),
      };
    },
  },
} satisfies Components<Theme>['MuiPaper'];

export const MuiMenu = {
  styleOverrides: {
    paper: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 12,
        marginTop: 4,
        backgroundColor: isDark
          ? 'var(--mui-palette-neutral-800)'
          : 'var(--mui-palette-background-paper)',
        backgroundImage: 'none',
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'var(--mui-palette-divider)'}`,
        boxShadow: isDark
          ? '0 10px 32px rgba(0, 0, 0, 0.55), 0 2px 6px rgba(0, 0, 0, 0.35)'
          : '0 10px 32px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06)',
      };
    },
    list: { padding: '6px' },
  },
} satisfies Components<Theme>['MuiMenu'];

export const MuiMenuItem = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 8,
        margin: '2px 0',
        minHeight: 'auto',
        padding: '8px 12px',
        transition: 'background-color 140ms ease, color 140ms ease',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(16, 24, 40, 0.04)',
        },
        '&.Mui-selected': {
          backgroundColor: isDark ? 'rgba(117, 120, 255, 0.16)' : 'rgba(99, 91, 255, 0.08)',
          color: isDark ? 'var(--mui-palette-primary-light)' : 'var(--mui-palette-primary-main)',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(117, 120, 255, 0.22)' : 'rgba(99, 91, 255, 0.12)',
          },
        },
        '&.Mui-focusVisible': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(16, 24, 40, 0.06)',
        },
      };
    },
  },
} satisfies Components<Theme>['MuiMenuItem'];

export const MuiListItemButton = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 8,
        transition: 'background-color 140ms ease, color 140ms ease',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(16, 24, 40, 0.04)',
        },
        '&.Mui-selected': {
          backgroundColor: isDark ? 'rgba(117, 120, 255, 0.18)' : 'rgba(99, 91, 255, 0.10)',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(117, 120, 255, 0.24)' : 'rgba(99, 91, 255, 0.14)',
          },
        },
      };
    },
  },
} satisfies Components<Theme>['MuiListItemButton'];

export const MuiTableRow = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        transition: 'background-color 140ms ease',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(16, 24, 40, 0.025)',
        },
        '&.Mui-selected': {
          backgroundColor: isDark ? 'rgba(117, 120, 255, 0.10)' : 'rgba(99, 91, 255, 0.06)',
          '&:hover': {
            backgroundColor: isDark ? 'rgba(117, 120, 255, 0.16)' : 'rgba(99, 91, 255, 0.10)',
          },
        },
      };
    },
  },
} satisfies Components<Theme>['MuiTableRow'];

export const MuiOutlinedInput = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 10,
        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
        transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.14)' : 'var(--mui-palette-divider)',
          transition: 'border-color 160ms ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.24)' : 'var(--mui-palette-neutral-400)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: 'var(--mui-palette-primary-main)',
          borderWidth: 1,
        },
        '&.Mui-focused': {
          boxShadow: `0 0 0 3px ${
            isDark ? 'rgba(117, 120, 255, 0.22)' : 'rgba(99, 91, 255, 0.16)'
          }`,
        },
        '&.Mui-disabled': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(16, 24, 40, 0.03)',
        },
      };
    },
    input: {
      '&::placeholder': {
        opacity: 0.6,
      },
    },
  },
} satisfies Components<Theme>['MuiOutlinedInput'];

export const MuiInputLabel = {
  styleOverrides: {
    root: ({ theme }) => ({
      color: theme.palette.mode === 'dark' ? 'rgba(230, 237, 243, 0.7)' : undefined,
      '&.Mui-focused': {
        color: 'var(--mui-palette-primary-main)',
      },
    }),
  },
} satisfies Components<Theme>['MuiInputLabel'];

export const MuiDialog = {
  styleOverrides: {
    paper: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 16,
        backgroundImage: 'none',
        backgroundColor: isDark
          ? 'var(--mui-palette-neutral-800)'
          : 'var(--mui-palette-background-paper)',
        border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid transparent',
        boxShadow: isDark
          ? '0 24px 64px rgba(0, 0, 0, 0.7), 0 8px 24px rgba(0, 0, 0, 0.5)'
          : '0 24px 64px rgba(16, 24, 40, 0.18), 0 8px 24px rgba(16, 24, 40, 0.10)',
      };
    },
  },
} satisfies Components<Theme>['MuiDialog'];

export const MuiPopover = {
  styleOverrides: {
    paper: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 12,
        backgroundImage: 'none',
        backgroundColor: isDark
          ? 'var(--mui-palette-neutral-800)'
          : 'var(--mui-palette-background-paper)',
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'var(--mui-palette-divider)'}`,
        boxShadow: isDark
          ? '0 12px 36px rgba(0, 0, 0, 0.55), 0 2px 6px rgba(0, 0, 0, 0.35)'
          : '0 12px 36px rgba(16, 24, 40, 0.12), 0 2px 6px rgba(16, 24, 40, 0.06)',
      };
    },
  },
} satisfies Components<Theme>['MuiPopover'];

export const MuiTooltip = {
  styleOverrides: {
    tooltip: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        backgroundColor: isDark ? 'rgba(20, 24, 32, 0.95)' : 'rgba(33, 38, 54, 0.92)',
        color: '#ffffff',
        fontSize: '0.75rem',
        fontWeight: 500,
        borderRadius: 8,
        padding: '6px 10px',
        border: isDark ? '1px solid rgba(255, 255, 255, 0.10)' : 'none',
        backdropFilter: 'blur(4px)',
      };
    },
    arrow: ({ theme }) => ({
      color: theme.palette.mode === 'dark' ? 'rgba(20, 24, 32, 0.95)' : 'rgba(33, 38, 54, 0.92)',
    }),
  },
} satisfies Components<Theme>['MuiTooltip'];

export const MuiDivider = {
  styleOverrides: {
    root: ({ theme }) => ({
      borderColor:
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'var(--mui-palette-divider)',
    }),
  },
} satisfies Components<Theme>['MuiDivider'];

export const MuiChip = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 8,
        fontWeight: 500,
        '&.MuiChip-outlined': {
          borderColor: isDark ? 'rgba(255, 255, 255, 0.16)' : 'var(--mui-palette-divider)',
        },
      };
    },
  },
} satisfies Components<Theme>['MuiChip'];

export const MuiIconButton = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 10,
        transition: 'background-color 140ms ease, color 140ms ease',
        '&:hover': {
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(16, 24, 40, 0.05)',
        },
      };
    },
  },
} satisfies Components<Theme>['MuiIconButton'];

export const MuiSwitch = {
  styleOverrides: {
    track: ({ theme }) => ({
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.18)' : 'rgba(16, 24, 40, 0.22)',
      opacity: 1,
    }),
  },
} satisfies Components<Theme>['MuiSwitch'];

export const MuiCheckbox = {
  styleOverrides: {
    root: ({ theme }) => ({
      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : undefined,
    }),
  },
} satisfies Components<Theme>['MuiCheckbox'];

export const MuiAppBar = {
  styleOverrides: {
    root: ({ theme }) => ({
      backgroundImage: 'none',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'var(--mui-palette-neutral-900)'
          : 'var(--mui-palette-background-paper)',
    }),
  },
} satisfies Components<Theme>['MuiAppBar'];

export const MuiDrawer = {
  styleOverrides: {
    paper: ({ theme }) => ({
      backgroundImage: 'none',
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'var(--mui-palette-neutral-950)'
          : 'var(--mui-palette-background-paper)',
      borderRight:
        theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.06)' : undefined,
    }),
  },
} satisfies Components<Theme>['MuiDrawer'];

export const MuiAlert = {
  styleOverrides: {
    root: ({ theme }) => {
      const isDark = theme.palette.mode === 'dark';
      return {
        borderRadius: 12,
        ...(isDark && {
          backgroundImage: 'none',
        }),
      };
    },
  },
} satisfies Components<Theme>['MuiAlert'];
